import { openPaymentPage } from "@/lib/open-checkout";
import { DigitalConsentCheckbox } from "@/components/DigitalConsentCheckbox";
import { buildDigitalConsent } from "@/lib/digital-consent";
import { checkoutAttribution } from "@/lib/checkout-attribution";
import { localeOrigin } from "@/lib/locale";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  Crown,
  Loader2,
  CreditCard,
  ChevronRight,
  ShieldCheck,
  LogOut,
  Mail,
  Check,
} from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { getOnboardingState, type OnboardingState } from "@/lib/onboarding.functions";
import { createFoundingCheckoutSession } from "@/lib/membership.functions";
import { useTherapistOnlyMode } from "@/lib/launch-mode";
import { PaypalCheckoutButton } from "./membership";
import { VoucherField, type AppliedVoucher } from "@/components/membership/VoucherField";
import { formatCents } from "@/lib/vouchers";
import { supabase } from "@/integrations/supabase/client";
import { secureSignOut } from "@/lib/secure-sign-out";
import { track } from "@/lib/track";
import { trackFunnel, trackCheckoutFailed } from "@/lib/funnel";
import {
  formatPlanPrice,
  formatPlanCycle,
  formatPlanPriceShort,
  getPlan,
} from "@/lib/membership-plans";
import { adoptPlanFromUrl } from "@/lib/selected-plan";
import { LocalPriceNote } from "@/components/LocalPrice";
import { Button } from "@/components/ui/button";
import { StayInTouchCard } from "@/components/membership/StayInTouchCard";

/**
 * /complete-membership — the ONLY page a confirmed-but-unpaid Founding
 * Member should see after signing in. Server-side membership state is the
 * source of truth; this route re-fetches it and routes elsewhere if the
 * user doesn't belong here.
 */
export const Route = createFileRoute("/complete-membership")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Complete your Membership — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Your account is created and your email is confirmed. Complete your payment to activate your Recovery Trauma™ Membership.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Complete your Membership — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Complete your secure Recovery Trauma™ membership payment and activate access.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CompleteMembershipRoute,
});

function CompleteMembershipRoute() {
  const membership = useMembership();
  const navigate = useNavigate();
  const fetchState = useServerFn(getOnboardingState);
  const preLaunch = useTherapistOnlyMode();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [resolved, setResolved] = useState(false);

  // Route guests to sign-in. Wait for hydration so the first client render's
  // signed-out snapshot never bounces a signed-in member.
  useEffect(() => {
    if (membership.loading) return;
    // Only when auth has definitively confirmed there is no session.
    if (membership.canRedirectToSignIn) {
      navigate({ to: "/auth", replace: true });
    }
  }, [membership.loading, membership.canRedirectToSignIn, navigate]);


  // Load server-side onboarding/membership state.
  useEffect(() => {
    let alive = true;
    if (!membership.isAuthed || membership.loading) return;
    // Admins get shipped to dashboard directly.
    if (membership.isAdmin) {
      navigate({ to: "/dashboard", replace: true });
      return;
    }
    fetchState({ data: undefined as never })
      .then((s) => {
        if (!alive) return;
        setState(s);
        setResolved(true);
      })
      .catch(() => {
        if (!alive) return;
        setResolved(true);
      });
    return () => {
      alive = false;
    };
  }, [membership.isAuthed, membership.loading, membership.isAdmin, fetchState, navigate]);

  // Route based on the server-side truth. Email confirmation is NOT a gate
  // here — a new account goes straight from create-account to choosing a
  // payment method; the confirmation email can be actioned any time.
  useEffect(() => {
    if (!resolved || !state) return;
    if (state.is_member) {
      if (preLaunch) navigate({ to: "/youre-in", replace: true });
      else navigate({ to: "/dashboard", replace: true });
    }
  }, [resolved, state, preLaunch, navigate]);

  // Spinner-first gate — never flash any content before we know the state.
  const showSpinner =
    membership.loading ||
    !membership.isAuthed ||
    membership.isAdmin ||
    !resolved ||
    (state && state.is_member);


  if (showSpinner) {
    return (
      <div className="flex min-h-[70svh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" />
      </div>
    );
  }

  return <PaymentCard />;
}

function PaymentCard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const createCheckout = useServerFn(createFoundingCheckoutSession);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Express digital-content consent, captured before any payment button opens.
  const [digitalConsent, setDigitalConsent] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [planId, setPlanId] = useState<string | null>(null);
  const [failureReason, setFailureReason] = useState<string | null>(null);
  // Set when someone comes back from PayPal without finishing (including a
  // mis-tap), so both ways to pay are obviously still open.
  const [cancelled, setCancelled] = useState(false);
  const [voucher, setVoucher] = useState<AppliedVoucher | null>(null);
  const plan = getPlan(planId);

  // A code is checked against one plan, so changing plan clears it.
  useEffect(() => {
    setVoucher(null);
  }, [plan.id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPlanId(adoptPlanFromUrl(params.get("plan")));
    const reason = params.get("reason");
    if (reason) setFailureReason(reason);
    if (params.get("cancelled")) setCancelled(true);
  }, []);

  useEffect(() => {
    trackFunnel(
      "page_view",
      { source: "complete_membership", plan: plan.id, dedupeKey: "complete_membership_view" },
      { surface: "complete_membership", arrived_after_failure: !!failureReason },
    );
  }, [plan.id, failureReason]);

  async function handleStripe() {
    setErrorMsg(null);
    if (!digitalConsent) {
      setErrorMsg("Please tick the box to confirm you want immediate access before paying.");
      return;
    }
    track(
      "plan_select",
      { plan: plan.id, price_usd: plan.priceCents / 100 },
      { source: "complete_membership_page" },
    );
    setLoading(true);
    trackFunnel("payment_method_selected", {
      source: "complete_membership",
      provider: "stripe",
      plan: plan.id,
      price_usd: plan.priceCents / 100,
    });
    track(
      "checkout_start",
      { plan: plan.id },
      { source: "complete_membership_page" },
    );
    trackFunnel(
      "redirect_started",
      {
        source: "complete_membership",
        provider: "stripe",
        plan: plan.id,
        price_usd: plan.priceCents / 100,
      },
      { price_id: plan.stripePriceId },
    );
    let failure: string | null = null;
    try {
      const origin = localeOrigin();
      const res = await createCheckout({
        data: {
          plan_id: plan.id,
          digital_consent: buildDigitalConsent(),
          attribution: checkoutAttribution(),
          ...(voucher?.code ? { voucher_code: voucher.code } : {}),
          success_url: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/complete-membership`,
        } as never,
      });
      if (res && (res as { ok?: boolean }).ok && (res as { url?: string }).url) {
        trackFunnel(
          "checkout_session_created",
          {
            source: "complete_membership",
            provider: "stripe",
            plan: plan.id,
            price_usd: plan.priceCents / 100,
          },
          { price_id: plan.stripePriceId },
        );
        openPaymentPage((res as { url: string }).url);
        return;
      }
      failure =
        (res as { message?: string })?.message ??
        (res as { status?: string })?.status ??
        "checkout_session_not_created";
      setErrorMsg(
        (res as { message?: string })?.message ??
          "We couldn't start checkout. Please try again in a moment.",
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      failure = msg;
      setErrorMsg(
        msg && msg !== "[object Object]" && msg.length < 400
          ? msg
          : "Something went wrong starting checkout. Please try again.",
      );
    } finally {
      setLoading(false);
    }
    if (failure) {
      trackCheckoutFailed({
        source: "complete_membership",
        provider: "stripe",
        plan: plan.id,
        price_usd: plan.priceCents / 100,
        price_id: plan.stripePriceId,
        reason: failure,
        stage: "session_create",
        fallback: "/complete-membership",
      });
    }
  }

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await secureSignOut(queryClient);
    } catch { /* noop */ }
    navigate({ to: "/", replace: true });
  }

  return (
    <div className="min-h-dvh bg-gradient-to-b from-[oklch(0.985_0.012_85)] via-background to-[oklch(0.96_0.03_82)] px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-2xl">
        <div className="relative overflow-hidden rounded-[2rem] border border-gold/30 bg-card p-6 shadow-sm sm:p-10">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)]">
              <Crown className="h-5 w-5 text-foreground" strokeWidth={2.25} />
            </div>
            <h1 className="mt-4 font-display text-2xl leading-tight text-foreground sm:text-3xl">
              Complete your Membership
            </h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground/75">
              Your account is ready. Complete your Recovery Trauma™ membership.
            </p>

            {failureReason ? (
              <div
                role="status"
                className="mx-auto mt-4 max-w-md rounded-2xl border border-amber-300/70 bg-amber-50/80 px-4 py-3 text-start text-[12px] leading-relaxed text-amber-900/90 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100/90"
              >
                Your account was created, but we couldn't open the payment page
                automatically. Nothing has been charged — you can complete your
                membership below.
                <span className="mt-1 block text-[11px] opacity-70">
                  Reason: {failureReason.slice(0, 160)}
                </span>
              </div>
            ) : null}

            {cancelled ? (
              <div
                role="status"
                className="mx-auto mt-4 max-w-md rounded-2xl border border-gold/40 bg-cream/60 px-4 py-3 text-start text-[12px] leading-relaxed text-foreground/85 dark:bg-cream/10"
              >
                No payment was taken. You can pay by card or with PayPal below —
                whichever suits you.
              </div>
            ) : null}

            <div className="mt-6 flex items-baseline justify-center gap-2">
              {voucher ? (
                <span className="font-serif text-2xl text-muted-foreground line-through">
                  {formatPlanPrice(plan)}
                </span>
              ) : null}
              <span className="font-serif text-5xl text-foreground">
                {voucher ? formatCents(voucher.final_cents) : formatPlanPrice(plan)}
              </span>
              <span className="text-sm text-muted-foreground">{formatPlanCycle(plan)}</span>
            </div>
            <LocalPriceNote
              cents={voucher ? voucher.final_cents : plan.priceCents}
              className="mt-1 text-xs text-muted-foreground"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {plan.shortName} · Cancel anytime — renews automatically until cancelled.
            </p>
            <p className="mt-2 text-xs">
              <Link
                to="/choose-membership"
                className="font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
              >
                Change plan
              </Link>
            </p>

            <ul className="mx-auto mt-5 flex max-w-md flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-foreground/70">
              <li className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[var(--gold-deep)]" strokeWidth={3} />
                Cancel anytime
              </li>
              <li className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
                Secure payment
              </li>
              <li className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-[var(--gold-deep)]" strokeWidth={3} />
                Full access to every tool, workshop and the community
              </li>
            </ul>
          </div>



          <div className="mt-4 space-y-3">
            <VoucherField
              planId={plan.id}
              applied={voucher}
              onApplied={setVoucher}
              className="text-center"
            />

            <div className="rounded-xl border border-gold/25 bg-cream/50 p-3 dark:bg-cream/5">
              <DigitalConsentCheckbox checked={digitalConsent} onChange={setDigitalConsent} id="digital-consent-complete" />
            </div>

            <p className="pt-1 text-center font-display text-sm text-foreground">
              Choose how you’d like to pay
            </p>

            <Button
              type="button"
              onClick={handleStripe}
              disabled={loading || !digitalConsent}
              className="group min-h-[64px] w-full overflow-hidden whitespace-normal rounded-xl px-4 shadow-sm disabled:opacity-60"
            >
              <span className="pay-icon-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" strokeWidth={2.25} />}
              </span>
              <span className="min-w-0 flex-1 text-start">
                <span className="pay-label block font-display text-[15px] font-semibold leading-tight">Pay by card</span>
                <span className="pay-sub mt-0.5 block text-xs font-normal">
                  Secure checkout · {voucher ? formatCents(voucher.final_cents) : formatPlanPriceShort(plan)}
                </span>
              </span>
              <ChevronRight className="h-5 w-5 shrink-0 transition group-hover:translate-x-0.5" />
            </Button>


            {errorMsg ? (
              <p className="text-center text-sm text-destructive" role="alert">
                {errorMsg}
              </p>
            ) : null}

            {/* PayPal offers the same three plans as Stripe. */}
            <PaypalCheckoutButton planId={plan.id} voucherCode={voucher?.code ?? null} consentGiven={digitalConsent} />
          </div>

          <p className="mt-5 flex items-center justify-center gap-1.5 text-[12px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
            Secure payment processed by Stripe or PayPal.
          </p>


          <div className="mt-8 flex flex-col items-center gap-2 border-t border-gold/20 pt-6 text-xs text-muted-foreground">
            <a
              href="mailto:info@recoverytrauma.com"
              className="inline-flex items-center gap-1.5 font-medium text-[var(--gold-deep)] hover:underline"
            >
              <Mail className="h-3.5 w-3.5" /> Contact support
            </a>
            <button
              type="button"
              onClick={handleSignOut}
              disabled={signingOut}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-foreground/70 transition hover:text-foreground disabled:opacity-60"
            >
              {signingOut ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <LogOut className="h-3.5 w-3.5" />
              )}
              Sign out
            </button>
          </div>
        </div>
        <StayInTouchCard />
      </div>
    </div>
  );
}
